import React, { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Zap, Crown, Gem } from "lucide-react";

export const PLAN_THEME = {
  pro: { name: "PRO", icon: Zap, c1: "#FACC15", c2: "#F59E0B", glow: "rgba(250,204,21,.45)", exports: 40, res: "1080p · 60 fps" },
  premium: { name: "PREMIUM", icon: Crown, c1: "#E879F9", c2: "#8B5CF6", glow: "rgba(168,85,247,.5)", exports: 75, res: "2K · 60 fps" },
  vip: { name: "VIP", icon: Gem, c1: "#E9D5FF", c2: "#22D3EE", glow: "rgba(34,211,238,.45)", exports: 150, res: "4K · 60 fps" },
};

const Burst = ({ t }) => {
  const parts = useMemo(() => Array.from({ length: 46 }, (_, i) => ({
    a: (i / 46) * Math.PI * 2 + Math.random() * 0.3, d: 180 + Math.random() * 420, s: 4 + Math.random() * 9, c: i % 3 ? t.c1 : t.c2, r: Math.random() * 360,
  })), [t]);
  return parts.map((p, i) => (
    <motion.span key={i} className="absolute left-1/2 top-1/2 rounded-sm" style={{ width: p.s, height: p.s * 0.45, background: p.c }}
      initial={{ x: 0, y: 0, opacity: 0, rotate: 0 }} animate={{ x: Math.cos(p.a) * p.d, y: Math.sin(p.a) * p.d, opacity: [0, 1, 0], rotate: p.r + 360 }}
      transition={{ delay: 0.35, duration: 1.6, ease: [0.1, 0.8, 0.2, 1] }} />
  ));
};

// Full-screen 2.5s plan reveal shown after the user confirms a successful purchase.
export const PlanReveal = ({ plan, onDone }) => {
  const t = PLAN_THEME[plan] || PLAN_THEME.pro;
  const Icon = t.icon;
  useEffect(() => { const id = setTimeout(onDone, 2500); return () => clearTimeout(id); }, [onDone]);
  return (
    <motion.div className="fixed inset-0 z-[200] grid place-items-center overflow-hidden bg-[#060606]" style={{ backgroundColor: "#060606" }} data-testid="plan-reveal"
      initial={{ opacity: 0 }} animate={{ opacity: [0, 1, 1, 0] }} transition={{ duration: 2.5, times: [0, 0.08, 0.86, 1] }}>
      <motion.div className="absolute h-[140vmax] w-[140vmax] rounded-full" style={{ background: `conic-gradient(from 0deg, transparent, ${t.glow}, transparent 30%, transparent 50%, ${t.glow}, transparent 80%)` }}
        initial={{ rotate: 0, scale: 0.6, opacity: 0 }} animate={{ rotate: 120, scale: 1, opacity: 0.55 }} transition={{ duration: 2.5, ease: "easeOut" }} />
      <motion.div className="absolute h-[60vmin] w-[60vmin] rounded-full blur-[90px]" style={{ background: t.glow }}
        initial={{ scale: 0 }} animate={{ scale: [0, 1.4, 1.1] }} transition={{ duration: 1.2 }} />
      <Burst t={t} />
      <div className="relative flex flex-col items-center text-center px-6">
        <motion.div initial={{ scale: 0, rotate: -40 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 220, damping: 14, delay: 0.1 }}
          className="grid h-20 w-20 sm:h-24 sm:w-24 place-items-center rounded-3xl shadow-2xl" style={{ background: `linear-gradient(135deg, ${t.c1}, ${t.c2})`, boxShadow: `0 20px 80px -10px ${t.glow}` }}>
          <Icon className="h-10 w-10 sm:h-12 sm:w-12 text-black" strokeWidth={2.5} />
        </motion.div>
        <motion.p className="mt-8 text-xs sm:text-sm font-bold tracking-[0.5em] text-zinc-400" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>BIENVENIDO A</motion.p>
        <motion.h1 data-testid="plan-reveal-name" className="mt-2 font-black leading-none tracking-tight text-[22vw] sm:text-[160px]"
          style={{ backgroundImage: `linear-gradient(180deg, #fff 10%, ${t.c1} 55%, ${t.c2})`, WebkitBackgroundClip: "text", color: "transparent", filter: `drop-shadow(0 10px 40px ${t.glow})` }}
          initial={{ scale: 1.6, opacity: 0, letterSpacing: "0.4em" }} animate={{ scale: 1, opacity: 1, letterSpacing: "-0.02em" }} transition={{ delay: 0.3, duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}>
          {t.name}
        </motion.h1>
        <motion.div className="mt-4 h-[3px] rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${t.c1}, transparent)` }}
          initial={{ width: 0 }} animate={{ width: 280 }} transition={{ delay: 0.8, duration: 0.7 }} />
        <motion.p className="mt-5 text-sm sm:text-base text-zinc-300" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
          Vit<span style={{ color: t.c1 }}>led</span> · {t.exports} exportaciones/mes · {t.res}
        </motion.p>
      </div>
    </motion.div>
  );
};
